<script setup>
import { ref, computed, watch, provide } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  useParticipants,
  getNotarialItem,
  getTransactionLinks,
  generatePdfFromTipTap,
} from "@/entities/transaction";
import { getInheritanceItem } from "@/entities/inheritance/api";
import { useIsMobile } from "@/shared/lib/composables/useIsMobile";
import { CATEGORY_CODES } from "@/shared/lib/constants";
import { toast } from "@/shared/store/alertStore";
import { useAvailableActions } from "@/features/notarial-actions";
import { AVAILABLE_ACTIONS_KEY } from "@/pages/admin/client_cabinet/availableActionsKey";

import LoadingOverlay from "@/shared/ui/custom/components/LoadingOverlay.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import ClientCabinetDocumentTab from "@/pages/admin/client_cabinet/tabs/ClientCabinetDocumentTab.vue";
import ClientCabinetAttachmentsTab from "@/pages/admin/client_cabinet/tabs/ClientCabinetAttachmentsTab.vue";
import NotarialActionHistoryView from "@/pages/admin/client_cabinet/components/shared/NotarialActionHistoryView.vue";
import { PaymentTab } from "@/features/payments";
import ClientCabinetSignTab from "@/pages/admin/client_cabinet/tabs/ClientCabinetSignTab.vue";
import ClientDocumentHeaderCard from "@/pages/admin/client_cabinet/components/mobile/ClientDocumentHeaderCard.vue";
import BottomSheet from "@/shared/ui/bottom-sheet/BottomSheet.vue";

import { getReferenceListByCodes } from "@/entities/directory";

import Button from "@/shared/ui/button/Button.vue";
import StatusActionPicker from "@/pages/admin/enforcement_inscriptions/components/StatusActionPicker.vue";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const loading = ref(false);
const activeTab = ref("document");
const form = ref({});
const links = ref([]);
const references = ref([]);
const isConsentChecked = ref(false);
const showOperationsSheet = ref(false);
const { isMobile } = useIsMobile();

const uuid = computed(() => route.params.id);
const isInheritance = computed(
  () => route.query.template_category_code === CATEGORY_CODES.SVID_PRAVO_NASLED
);

const { participants, fetchParticipants } = useParticipants(uuid);

const availableActionsSource = computed(() => ({
  transactionId: form.value?.id || null,
}));
const { availableActions, refresh: refreshAvailableActions } =
  useAvailableActions(availableActionsSource);

provide(AVAILABLE_ACTIONS_KEY, {
  availableActions,
  refresh: refreshAvailableActions,
});

const tabs = computed(() => ({
  document: { label: t("Документ"), icon: "huge-eye" },
  attachments: { label: t("Вложения"), icon: "huge-file-attach" },
  payment: { label: t("Оплата"), icon: "huge-document-info" },
  sign: { label: t("Подписание"), icon: "huge-editor-magic-wand" },
  history: { label: t("История"), icon: "huge-history" },
}));

const availableDownloadLanguages = computed(() => {
  const langLabels = { ru: "Русский", kz: "Қазақша", en: "English" };
  return getAvailableTemplatePayloads().map((p) => ({
    lang: p.lang,
    label: langLabels[p.lang] || p.lang,
  }));
});

const tabComponents = {
  document: ClientCabinetDocumentTab,
  attachments: ClientCabinetAttachmentsTab,
  payment: PaymentTab,
  sign: ClientCabinetSignTab,
  history: NotarialActionHistoryView,
};

const currentTabComponent = computed(
  () => tabComponents[activeTab.value] || null
);

const templateLabel = computed(() => {
  const code = form.value?.template_code;
  if (!code) return "";
  const reference = references.value.find((item) => item.code === code);
  return reference?.name || "";
});

const operationsDisabled = computed(() => !isConsentChecked.value);

const isPaid = computed(() => {
  const status =
    form.value?.payment_status ||
    form.value?.payment_status_code ||
    form.value?.payment?.status ||
    "";
  const normalized = String(status).toLowerCase();
  return (
    form.value?.is_paid === true ||
    form.value?.paid === true ||
    form.value?.payment?.is_paid === true ||
    normalized === "paid" ||
    normalized === "оплачено"
  );
});

const payActionKey = computed(() => (isPaid.value ? "sign" : "pay"));

const showOperationsBar = computed(
  () => activeTab.value === "document" || activeTab.value === "attachments"
);

watch(
  () => route.query.tab,
  (tab) => {
    activeTab.value = tab || "document";
  },
  { immediate: true }
);

async function loadData() {
  loading.value = true;
  try {
    const data = isInheritance.value
      ? await getInheritanceItem(uuid.value)
      : await getNotarialItem(uuid.value);
    if (data?.success && data.payload) {
      form.value = data.payload;
    }
    await Promise.all([fetchParticipants(), loadLinks()]);
    await loadReferences();
  } finally {
    loading.value = false;
  }
}

async function loadLinks() {
  if (!form.value?.id) return;
  const data = await getTransactionLinks(form.value.id);
  if (data?.success) {
    links.value = data.payload?.items || [];
  }
}

async function loadReferences() {
  const code = form.value?.template_code;
  if (!code) return;
  const data = await getReferenceListByCodes({ codes: [code] });
  if (data?.success) {
    references.value = data.payload?.items || [];
  }
}

function changeTab(tab) {
  router.replace({
    query: {
      ...route.query,
      tab,
    },
  });
}

function openOperations() {
  if (operationsDisabled.value) {
    toast.warning(t("Сначала подтвердите согласие с документом"));
    return;
  }
  showOperationsSheet.value = true;
}

function closeOperations() {
  showOperationsSheet.value = false;
}

async function handleOperation(action) {
  showOperationsSheet.value = false;
  if (action === "download") {
    return;
  }
  if (action === "sign") {
    if (!isPaid.value) {
      activeTab.value = "payment";
      toast.warning(t("Сначала оплатите документ"));
      return;
    }
    activeTab.value = "sign";
    return;
  }
  if (action === "pay") {
    activeTab.value = "payment";
  }
}

function getAvailableTemplatePayloads() {
  const candidates = [
    { lang: "ru", json: form.value?.content },
    { lang: "kz", json: form.value?.content_kz },
    { lang: "en", json: form.value?.content_en },
  ];
  return candidates
    .map((item) => {
      let data = item.json;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          data = null;
        }
      }
      return { lang: item.lang, data };
    })
    .filter((item) => item.data && item.data.type === "doc");
}

function createFileName(lang) {
  const base =
    form.value?.number || form.value?.uuid || form.value?.id || t("Документ");
  return `${base}_${lang}.pdf`;
}

function handleDownloadCommand(command) {
  const payloads = getAvailableTemplatePayloads();
  if (command !== "all") {
    const hasContent = payloads.some((p) => p.lang === command);
    if (!hasContent) {
      toast.error(t("Контент для данного языка пустой"));
      return;
    }
  }
  if (!payloads.length) {
    toast.error(t("Нет данных для скачивания"));
    return;
  }
  if (command === "all") {
    downloadDocuments();
  } else {
    downloadDocuments(command);
  }
}

async function downloadDocuments(lang) {
  const language = lang || "all";

  const response = await generatePdfFromTipTap(uuid.value, { lang: language });
  if (!response?.data) {
    toast.error(t("Не удалось сформировать PDF"));
    return;
  }

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = createFileName(language);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// created logic
if (uuid.value) {
  loadData();
}
</script>

<template>
  <div class="client-cabinet-item">
    <loading-overlay v-if="loading" :value="loading" />

    <div v-if="!loading" class="mobile-header-card">
      <ClientDocumentHeaderCard
        :form="form"
        :participants="participants"
        :links="links"
        :template-label="templateLabel"
      />
    </div>

    <div v-if="!loading" class="tabs-card">
      <BaseTabs :tabs="tabs" :active-tab="activeTab" @change:tab="changeTab">
        <template #action>
          <div class="tabs-actions">
            <el-dropdown trigger="click" @command="handleDownloadCommand">
              <Button variant="outline">
                <svg-icon name="huge-download" class="w-5 h-5" />
              </Button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="item in availableDownloadLanguages"
                    :key="item.lang"
                    :command="item.lang"
                  >
                    {{ item.label }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="availableDownloadLanguages.length > 1"
                    divided
                    command="all"
                  >
                    {{ $t("Скачать все языки") }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>

            <StatusActionPicker
              v-if="form?.id"
              :transaction-id="form.id"
              :current-status="form?.status || form?.current_status || ''"
              @applied="loadData"
            />
          </div>
        </template>
      </BaseTabs>
    </div>

    <component
      :is="currentTabComponent"
      :form="form"
      :uuid="uuid"
      :participants="participants"
      :loading="loading"
      :consent-checked="isConsentChecked"
      :is-mobile="isMobile"
      :isConsentChecked="isConsentChecked"
      @update:consent="(val) => (isConsentChecked = val)"
      :template-label="templateLabel"
      class="tab-content"
      @request:sign="changeTab('sign')"
      @paid="loadData"
    />

    <div v-if="showOperationsBar" class="actions-bar">
      <Button variant="default" class="actions-button" @click="openOperations">
        {{ $t("Операции") }}
      </Button>
    </div>
  </div>

  <BottomSheet
    v-model="showOperationsSheet"
    :title="$t('Операции')"
    height="auto"
    @close="closeOperations"
  >
    <div class="operations-list">
      <el-dropdown
        trigger="click"
        @command="handleDownloadCommand"
        class="w-full"
      >
        <Button variant="outline" class="w-full">
          {{ $t("Скачать") }}
        </Button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
              v-for="item in availableDownloadLanguages"
              :key="item.lang"
              :command="item.lang"
            >
              {{ item.label }}
            </el-dropdown-item>
            <el-dropdown-item
              v-if="availableDownloadLanguages.length > 1"
              divided
              command="all"
            >
              {{ $t("Скачать все языки") }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <Button variant="outline" @click="handleOperation(payActionKey)">
        {{ $t("Оплатить") }}
      </Button>
    </div>
  </BottomSheet>
</template>

<style scoped lang="scss">
.client-cabinet-item {
  @apply w-full h-full flex flex-col gap-6;
}

.mobile-header-card {
  display: none;
}

.tabs-card {
  @apply bg-card rounded-3xl;
}

.tabs-actions {
  @apply flex items-center gap-4;
}

.tab-content {
  @apply w-full flex-1;
}

.actions-bar {
  display: none;
}

.actions-button {
  @apply w-full;
}

.operations-list {
  @apply flex flex-col gap-3;
}

@media (max-width: 900px) {
  .tabs-card {
    @apply rounded-2xl;
  }

  .mobile-header-card {
    display: block;
  }

  .tabs-actions {
    display: none;
  }

  .actions-bar {
    @apply fixed left-6 right-6 bottom-0  pt-2 pb-4;
    @apply backdrop-blur-sm rounded;
    z-index: 10;
    display: block;
  }
}
</style>
